package org.chiangmeng.qfast.core.ui.staff

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import org.chiangmeng.qfast.core.domain.ports.QueueRepository
import org.chiangmeng.qfast.core.domain.ports.TextToSpeechService
import org.chiangmeng.qfast.core.domain.models.TicketPrintData
import org.chiangmeng.qfast.core.domain.ports.AuthRepository
import org.chiangmeng.qfast.core.domain.ports.TicketPrinterService
import org.chiangmeng.qfast.core.util.getCurrentTimeString
import org.chiangmeng.qfast.core.domain.models.AppTheme
import org.chiangmeng.qfast.core.domain.models.QueueTicket
import org.chiangmeng.qfast.core.util.getCurrentEpochMillis

class StaffViewModel(
    private val queueRepository: QueueRepository,
    private val ttsService: TextToSpeechService? = null,
    private val printerService: TicketPrinterService? = null,
    private val authRepository: AuthRepository? = null
) : ScreenModel {
    private val _state = MutableStateFlow(StaffContract.State())
    val state: StateFlow<StaffContract.State> = _state.asStateFlow()

    private var currentShopId: String? = null
    private var observeShopJob: Job? = null
    private var observeQueuesJob: Job? = null
    private val currentDeviceId = "${getCurrentEpochMillis()}_${(1000..9999).random()}"

    fun handleIntent(intent: StaffContract.Intent) {
        when (intent) {
            is StaffContract.Intent.LoadData -> loadData(intent.shopId)
            is StaffContract.Intent.NextQueue -> {
                val shopId = currentShopId ?: return
                nextQueue(shopId, intent.category)
            }
            is StaffContract.Intent.IssueTicket -> {
                val shopId = currentShopId ?: return
                issueTicket(shopId, intent.category)
            }
            StaffContract.Intent.ResetQueue -> performAction {
                queueRepository.resetQueue(it)
                updateHistory(it)
            }
            is StaffContract.Intent.UpdateShopInfo -> performAction {
                queueRepository.updateShopInfo(
                    it,
                    intent.name,
                    intent.adText,
                    intent.menuUrl,
                    intent.openTime,
                    intent.closeTime,
                    intent.theme
                )
            }
            is StaffContract.Intent.UpdateCategories -> performAction {
                queueRepository.updateShopCategories(it, intent.categories)
            }
            is StaffContract.Intent.ChangeTheme -> {
                // themeManager removed
            }
            is StaffContract.Intent.UpdateShopStatus -> performAction { 
                queueRepository.updateShopStatus(it, intent.isActive)
            }
            is StaffContract.Intent.CompleteQueue -> completeQueue(intent.shopId, intent.ticketNumber)
            is StaffContract.Intent.RecallQueue -> recallQueue(intent.shopId, ticketNumber = intent.ticketNumber)
            is StaffContract.Intent.SkipQueue -> skipQueue(intent.shopId, ticketNumber = intent.ticketNumber)
            is StaffContract.Intent.DeleteAccount -> deleteAccount(intent.onSuccess)
            is StaffContract.Intent.SignOut -> signOut(intent.onSuccess)
            StaffContract.Intent.ClearNewlyIssuedTicket, 
            StaffContract.Intent.DismissTicketDialog -> {
                _state.update { it.copy(newlyIssuedTicket = null) }
            }
            is StaffContract.Intent.PrintSpecificTicket -> {
                printTicket(intent.ticket)
            }
            is StaffContract.Intent.SelectTicketForQr -> {
                _state.update { it.copy(newlyIssuedTicket = intent.ticket) }
            }
        }
    }

    private fun loadData(shopId: String) {
        currentShopId = shopId
        observeShopJob?.cancel()
        observeQueuesJob?.cancel()

        screenModelScope.launch {
            queueRepository.claimShopDevice(shopId, currentDeviceId)
        }

        observeShopJob = screenModelScope.launch(Dispatchers.Main) {
            queueRepository.observeShop(shopId).collect { shop ->
                println("QFAST_LOCK_LOG: [3. ViewModel Compare] -> currentDeviceId: $currentDeviceId | incoming activeDeviceId: ${shop?.activeDeviceId}")
                if (shop != null && !shop.activeDeviceId.isNullOrBlank() && shop.activeDeviceId != currentDeviceId) {
                    println("QFAST_LOCK_LOG: [4. MISMATCH DETECTED] -> Triggering Force Logout! (current: $currentDeviceId vs incoming: ${shop.activeDeviceId})")
                    observeShopJob?.cancel()
                    observeQueuesJob?.cancel()
                    _state.update { it.copy(isForceLoggedOut = true) }
                    println("QFAST_LOCK_LOG: [5. State Updated] -> isForceLoggedOut set to true")
                    return@collect
                }
                val theme = try {
                    shop?.theme?.let { AppTheme.valueOf(it) } ?: AppTheme.AMBER
                } catch (_: Exception) {
                    AppTheme.AMBER
                }
                _state.update { it.copy(shop = shop, currentTheme = theme) }
            }
        }

        observeQueuesJob = screenModelScope.launch(Dispatchers.Main) {
            queueRepository.observeActiveQueues(shopId).collect { queues ->
                _state.update { it.copy(queues = queues) }
            }
        }

        updateHistory(shopId)
    }

    private fun updateHistory(shopId: String) {
        screenModelScope.launch {
            val historyList = queueRepository.getQueueHistory(shopId)
            _state.update { it.copy(history = historyList) }
        }
    }

    private fun performAction(action: suspend (String) -> Unit) {
        val shopId = currentShopId ?: return
        screenModelScope.launch {
            action(shopId)
        }
    }

    private fun issueTicket(shopId: String, category: String) {
        screenModelScope.launch {
            try {
                val ticketNumber = queueRepository.issueTicket(shopId, category)
                if (ticketNumber > 0) {
                    val timeString = getCurrentTimeString()
                    
                    val newTicket = QueueTicket(
                        number = ticketNumber,
                        category = category,
                        status = "waiting",
                        time = timeString
                    )

                    _state.update { it.copy(newlyIssuedTicket = newTicket) }
                }
            } catch (e: Exception) {
                _state.update { it.copy(errorMessage = e.message) }
            }
        }
    }

    private fun printTicket(ticket: QueueTicket) {
        val shopId = currentShopId ?: return
        val currentShop = _state.value.shop
        val shopName = currentShop?.name ?: "QFast Shop"
        val waitingCount = _state.value.queues.count { it.status.lowercase() == "waiting" }
        
        screenModelScope.launch {
            printerService?.printTicket(
                TicketPrintData(
                    shopName = shopName,
                    ticketNumber = ticket.number,
                    category = ticket.category,
                    issueTime = ticket.time,
                    waitingCount = waitingCount,
                    customerUrl = "https://qfast.cmdevstudio.tech/?shop=${shopId}&q=${ticket.category}${ticket.number}"
                )
            )
        }
    }

    private fun nextQueue(shopId: String, category: String) {
        screenModelScope.launch {
            try {
                val servedNumber = queueRepository.nextQueue(shopId, category)
                if (servedNumber != null) {
                    ttsService?.speak("ขอเชิญหมายเลข $category $servedNumber")
                }
            } catch (e: Exception) {
                _state.update { it.copy(errorMessage = e.message) }
            }
        }
    }

    private fun completeQueue(shopId: String, ticketNumber: Int) {
        screenModelScope.launch {
            try {
                queueRepository.completeQueue(shopId, ticketNumber)
            } catch (e: Exception) {
                _state.update { it.copy(errorMessage = e.message) }
            }
        }
    }

    private fun recallQueue(shopId: String, ticketNumber: Int) {
        screenModelScope.launch {
            try {
                val ticket = _state.value.queues.find { it.number == ticketNumber }
                val cat = ticket?.category ?: ""
                queueRepository.recallQueue(shopId, ticketNumber)
                ttsService?.speak("ขอเชิญหมายเลข $cat$ticketNumber ค่ะ")
            } catch (e: Exception) {
                _state.update { it.copy(errorMessage = e.message) }
            }
        }
    }

    private fun skipQueue(shopId: String, ticketNumber: Int) {
        screenModelScope.launch {
            try {
                queueRepository.skipQueue(shopId, ticketNumber)
            } catch (e: Exception) {
                _state.update { it.copy(errorMessage = e.message) }
            }
        }
    }

    private fun deleteAccount(onSuccess: () -> Unit) {
        val shopId = currentShopId ?: return
        screenModelScope.launch {
            try {
                println("QFAST_AUTH_DEBUG: shopId = $shopId, authRepository = $authRepository")
                queueRepository.deleteShop(shopId)
                println("QFAST_AUTH_DEBUG: deleteShop success, calling authRepository.deleteAccount()")
                val result = authRepository?.deleteAccount()
                println("QFAST_AUTH_DEBUG: deleteAccount result = $result")
                result?.getOrThrow()
                authRepository?.signOut()
                println("QFAST_AUTH_DEBUG: All deletion completed successfully")
                withContext(Dispatchers.Main) {
                    onSuccess()
                }
            } catch (e: Exception) {
                println("QFAST_AUTH_DEBUG: ERROR occurred -> ${e::class.simpleName}: ${e.message}")
                _state.update { it.copy(errorMessage = e.message) }
            }
        }
    }

    private fun signOut(onSuccess: () -> Unit) {
        screenModelScope.launch {
            try {
                authRepository?.signOut()
                withContext(Dispatchers.Main) {
                    onSuccess()
                }
            } catch (e: Exception) {
                _state.update { it.copy(errorMessage = e.message) }
            }
        }
    }
}
